import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { denormalizeCreatePspToWalletDepositTransaction } from '@model/CreatePspToWalletDepositTransaction'
import { ThreeDSSessionDto } from '@model/ThreeDSSessionDto'

import { safeLocalStorage } from '@hooks/useStorage'

import { Loading } from '@shared/components/Loading/Loading'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import { getDepositBasePath } from './DepositFlowLayout/helpers'
import { DepositFormValues } from './DepositFlowLayout/types'
import {
  THREE_DS_CALLBACK_READY_STORAGE_KEY,
  THREE_DS_FORM_VALUES_STORAGE_KEY,
  THREE_DS_MESSAGE_AUTH_FAILED,
  THREE_DS_MESSAGE_DEPOSIT_FAILED,
  THREE_DS_MESSAGE_DEPOSIT_RESULT,
  THREE_DS_POPUP_WINDOW_NAME,
  THREE_DS_SESSION_ID_STORAGE_KEY,
  THREE_DS_SESSION_RESULT_STORAGE_KEY,
} from './constants/threeDS'
import { createDepositTransaction, getDepositErrorMessage } from './helpers'

type ThreeDSStage = 'processing' | 'submitting'

const appendHiddenFields = (values: DepositFormValues): DepositFormValues => {
  const hiddenFields = values.paymentProvider?.parameters.form.reduce<Record<string, string>>(
    (acc, field) => {
      if (field.type === 'hidden' && field.field) {
        acc[field.field] = String(field.value ?? '')
      }
      return acc
    },
    {}
  )

  return {
    ...values,
    fields: {
      ...values.fields,
      ...hiddenFields,
    },
  }
}

const appendThreeDSFields = (
  values: DepositFormValues,
  session: ThreeDSSessionDto
): DepositFormValues => {
  const threeDSFields: Record<string, string> = {
    three_ds_session_id: session.id,
    three_ds_card_enrollment_code: session.sessionCardEnrollmentCode ?? '',
    three_ds_authentication_value: session.sessionAuthenticationValue ?? '',
    three_ds_eci: session.sessionEci ?? '',
    three_ds_transaction_id: session.sessionDsTransactionId ?? '',
    three_ds_status_code: session.sessionStatusCode ?? '',
    three_ds_secure_flow: session.sessionSecureFlow ?? '',
  }

  return {
    ...values,
    fields: {
      ...values.fields,
      ...threeDSFields,
    },
  }
}

const clearThreeDSSessionStorage = () => {
  safeLocalStorage.removeItem(THREE_DS_SESSION_ID_STORAGE_KEY)
  safeLocalStorage.removeItem(THREE_DS_SESSION_RESULT_STORAGE_KEY)
}

const clearThreeDSStorage = () => {
  clearThreeDSSessionStorage()
  safeLocalStorage.removeItem(THREE_DS_FORM_VALUES_STORAGE_KEY)
}

const postThreeDSToOpener = (message: Record<string, unknown>): boolean => {
  if (window.opener && !window.opener.closed) {
    window.opener.postMessage(message, window.location.origin)
    return true
  }
  return false
}

const notifyThreeDSCallbackReady = (search: string): void => {
  safeLocalStorage.setJSON(THREE_DS_CALLBACK_READY_STORAGE_KEY, {
    search,
    ts: Date.now(),
  })
}

export const Deposit3DSCallbackPage: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const tracker = useDepositTracker()
  const [stage, setStage] = useState<ThreeDSStage>('processing')
  const hasSubmittedRef = useRef(false)

  const sessionId = useMemo(() => {
    const query = new URLSearchParams(location.search)
    return (
      query.get('sessionId') ||
      query.get('session_id') ||
      query.get('id') ||
      safeLocalStorage.getItem(THREE_DS_SESSION_ID_STORAGE_KEY)
    )
  }, [location.search])

  const storedValues = useMemo(
    () => safeLocalStorage.getJSON<DepositFormValues>(THREE_DS_FORM_VALUES_STORAGE_KEY),
    []
  )

  const shouldRedirectToOpener = useMemo(() => {
    const query = new URLSearchParams(location.search)
    return query.get('target') === 'opener'
  }, [location.search])

  const isThreeDSPopupWindow = useMemo(() => window.name === THREE_DS_POPUP_WINDOW_NAME, [])

  const navigateToThreeDSFailure = useCallback(
    (walletId?: string, tradingAccountId?: string, reasonCode = '') => {
      const didPost = postThreeDSToOpener({
        type: THREE_DS_MESSAGE_AUTH_FAILED,
        walletId,
        reason: reasonCode,
      })
      if (didPost) {
        clearThreeDSSessionStorage()
        window.close()
        return
      }

      tracker.failed({ message: getDepositErrorMessage(reasonCode) })
      clearThreeDSSessionStorage()

      const target = walletId ? 'wallet' : 'trading-account'
      const id = walletId || tradingAccountId

      if (id) {
        const dashboard = storedValues?.dashboardContext || 'traders-room'
        const pspId = storedValues?.paymentProvider?.id
        const basePath = getDepositBasePath(target, id, dashboard, pspId)

        navigate(
          `${basePath}/transaction/failed?3ds=failure&reason=${encodeURIComponent(reasonCode)}`
        )
      } else {
        const exitPath =
          storedValues?.dashboardContext === 'payment-agent'
            ? '/dashboard/payment-agent/wallets'
            : '/dashboard/traders-room/wallets'
        navigate(exitPath)
      }
    },
    [navigate, storedValues, tracker]
  )

  const handleThreeDSFailure = useCallback(
    (session: ThreeDSSessionDto) => {
      const walletId = storedValues?.wallet?.id
      const tradingAccountId = storedValues?.tradingAccount?.id
      const reasonCode =
        session.authenticationStatusReasonCode ||
        session.authentication?.authenticationStatusReasonCode ||
        ''
      navigateToThreeDSFailure(walletId, tradingAccountId, reasonCode)
    },
    [navigateToThreeDSFailure, storedValues]
  )

  const submitDeposit = useCallback(
    async (session: ThreeDSSessionDto, values: DepositFormValues) => {
      const withHiddenFields = appendHiddenFields(values)
      const withThreeDSFields = appendThreeDSFields(withHiddenFields, session)

      const target = withThreeDSFields.wallet ? 'wallet' : 'trading-account'
      const id = withThreeDSFields.wallet?.id || withThreeDSFields.tradingAccount?.id
      clearThreeDSStorage()

      try {
        const transaction = await apiClient.transactionDeposit(
          denormalizeCreatePspToWalletDepositTransaction(
            createDepositTransaction(withThreeDSFields, target),
            locale
          )
        )
        const didPost = postThreeDSToOpener({
          type: THREE_DS_MESSAGE_DEPOSIT_RESULT,
          data: transaction,
          values: withThreeDSFields,
        })
        if (didPost) {
          clearThreeDSStorage()
          window.close()
          return
        }

        tracker.submitted({ deposit_id: transaction.id })
        const dashboard = values.dashboardContext || 'traders-room'
        const pspId = values.paymentProvider?.id
        const basePath = getDepositBasePath(target, id!, dashboard, pspId)

        navigate(`${basePath}/transaction/completed`, {
          state: {
            data: transaction,
            providerCategoryId: values.paymentProvider.providerCategory.id,
          },
        })
      } catch (error: unknown) {
        const errorMessage = getDepositErrorMessage(error)
        const didPost = postThreeDSToOpener({
          type: THREE_DS_MESSAGE_DEPOSIT_FAILED,
          walletId: values.wallet?.id,
          reason: errorMessage,
        })
        if (didPost) {
          clearThreeDSStorage()
          window.close()
          return
        }

        tracker.failed({ message: errorMessage })
        const dashboard = values.dashboardContext || 'traders-room'
        const pspId = values.paymentProvider?.id
        const basePath = getDepositBasePath(target, id!, dashboard, pspId)

        navigate(`${basePath}/transaction/failed`)
      } finally {
        clearThreeDSStorage()
      }
    },
    [apiClient, locale, navigate, tracker]
  )

  useEffect(() => {
    let isCancelled = false

    const loadSession = async () => {
      const shouldHandOffToMainWindow = isThreeDSPopupWindow || shouldRedirectToOpener
      if (shouldHandOffToMainWindow) {
        notifyThreeDSCallbackReady(location.search)
        window.close()
        return
      }

      if (!sessionId) {
        navigate('/dashboard')
        return
      }

      let session: ThreeDSSessionDto
      try {
        session = await apiClient.getThreeDSSession(sessionId)
      } catch {
        if (!isCancelled) {
          navigateToThreeDSFailure(storedValues?.wallet?.id, '')
        }
        return
      }

      if (isCancelled) {
        return
      }

      safeLocalStorage.setJSON<ThreeDSSessionDto>(THREE_DS_SESSION_RESULT_STORAGE_KEY, session)

      const status =
        session.authenticationStatus?.toLowerCase() ??
        session.authentication?.authenticationStatus?.toLowerCase()
      if (status === 'failed') {
        handleThreeDSFailure(session)
        return
      }

      if (status === 'successful' || status === 'attempted') {
        if (!storedValues) {
          const dashboard = location.state?.dashboardContext || 'traders-room'
          const exitPath =
            dashboard === 'payment-agent'
              ? '/dashboard/payment-agent/wallets'
              : '/dashboard/traders-room/wallets'
          navigate(exitPath)
          return
        }
        if (hasSubmittedRef.current) {
          return
        }
        hasSubmittedRef.current = true
        setStage('submitting')
        await submitDeposit(session, storedValues)
        return
      }

      handleThreeDSFailure(session)
    }

    void loadSession()

    return () => {
      isCancelled = true
    }
  }, [
    apiClient,
    handleThreeDSFailure,
    isThreeDSPopupWindow,
    location.search,
    location.state?.dashboardContext,
    navigate,
    navigateToThreeDSFailure,
    sessionId,
    shouldRedirectToOpener,
    storedValues,
    submitDeposit,
  ])

  return (
    <Loading
      isLoading
      showLoadingIcon
      text={stage === 'processing' ? t('Processing authentication') : t('Submitting your deposit')}
    />
  )
}
